import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import * as Yup from 'yup'
import { Formik } from 'formik'

import { EmailIcon, Button, AlertContext, Typography, TextInput, ArrowRight } from '@astrid/components'

import { printApiMessage } from 'api/apiMessages'
import useThunkDispatch from 'hooks/useThunkDispatch'
import { asyncSignIn } from 'store/services/User'
import { ValidationRules } from 'utils/ValidationRules'
import { ReactComponent as TrinityVerticalLogo } from 'resources/graphics/trinity_vertical_logo.svg'
import { ReactComponent as PoweredByLogo } from 'resources/graphics/powered_by_logo.svg'

import './Login.scss'

const validationSchema = Yup.object().shape({
  username: ValidationRules.required,
  password: ValidationRules.required
})

const Login: React.FC = () => {
  const { showAlert } = useContext(AlertContext)

  const [isLoginFormOpen, setIsEmailFormOpen] = useState(false)

  const dispatch = useThunkDispatch()
  const location = useLocation()

  const initialValues = useMemo(() => {
    const params = new URLSearchParams(location.search)

    return {
      username: params.get('username') || '',
      password: params.get('password') || ''
    }
  }, [location.search])

  const handleSubmit = useCallback(
    async ({ username, password }, { setSubmitting }) => {
      try {
        await dispatch(asyncSignIn(username, password))
      } catch (error) {
        if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
          showAlert(printApiMessage(error.message))
        }
        setSubmitting(false)
      }
    },
    // eslint-disable-next-line
    []
  )

  // const onSignInWithGoogle = useCallback(async () => {
  //   try {
  //     await dispatch(asyncSocialSignIn(SocialAuthMethod.GOOGLE))

  //   } catch (error) {
  //     if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
  //       showAlert(printApiMessage(error.message))
  //     }
  //   }
  //   // eslint-disable-next-line
  // }, [])

  return (
    <div className="auth" role="main">
      <div className="auth__container">
        <TrinityVerticalLogo className="auth__trinity-logo" aria-hidden="true" focusable="false" />
        <PoweredByLogo className="auth__powered-by-logo" aria-hidden="true" focusable="false" />
        {isLoginFormOpen ? (
          <Formik
            initialValues={{
              username: initialValues.username,
              password: initialValues.password,
              keepSignedIn: false
            }}
            validationSchema={validationSchema}
            isInitialValid={false}
            onSubmit={handleSubmit}
          >
            {({ values, touched, isValid, errors, handleChange, setFieldTouched, handleSubmit, isSubmitting }) => {
              return (
                <form className="auth__form" aria-labelledby="login-title" onSubmit={handleSubmit}>
                  <div className="auth__form-header">
                    <button
                      type="button"
                      style={{ background: 'transparent', padding: 0, display: 'flex' }}
                      className="auth__back-button"
                      onClick={() => setIsEmailFormOpen(false)}
                      aria-label="Go back"
                    >
                      <ArrowRight className="auth__back-icon" aria-hidden="true" focusable="false" />
                    </button>
                    <Typography id="login-title" className="auth__title" variant="h3" component="h1">
                      Log in
                    </Typography>
                  </div>
                  <TextInput
                    id="username"
                    name="username"
                    label="Username or Email"
                    autoComplete="username"
                    value={values.username}
                    onChange={handleChange('username')}
                    error={touched.username && !!errors.username}
                    helperText={(touched.username && errors.username) || ''}
                    aria-invalid={touched.username && !!errors.username}
                    aria-describedby="username-error"
                    onBlur={() => setFieldTouched('username')}
                  />
                  {/* TODO-VV: Missing show/hide password feature */}
                  <TextInput
                    id="password"
                    name="password"
                    label="Password"
                    type="password"
                    value={values.password}
                    onChange={handleChange('password')}
                    error={touched.password && !!errors.password}
                    helperText={(touched.password && errors.password) || ''}
                    aria-invalid={touched.password && !!errors.password}
                    aria-describedby="password-error"
                    onBlur={() => setFieldTouched('password')}
                  />
                  <Button
                    className="auth__button-submit"
                    type="submit"
                    disabled={isSubmitting || !isValid}
                    aria-disabled={isSubmitting || !isValid}
                  >
                    {isSubmitting ? 'Logging in...' : 'Log in'}
                  </Button>
                </form>
              )
            }}
          </Formik>
        ) : (
          <>
            <Button
              variant="outlined"
              icon={<EmailIcon aria-hidden="true" focusable="false" />}
              label="Using username or email"
              onClick={() => setIsEmailFormOpen(true)}
            />
            {/* <div className="auth__divider">
              <div className="auth__divider-line" />
              <Typography variant="body" color="secondary">
                OR
              </Typography>
              <div className="auth__divider-line" />
            </div>
            <Button
              variant="outlined"
              icon={<GoogleLogo />}
              label="Continue with Google"
              onClick={onSignInWithGoogle}
            /> */}
          </>
        )}
        <Typography variant="body" color="secondary" className="auth__terms">
          By signing in to Astrid, you agree to our
          <br />
          <a
            target="_blank"
            href="./terms_and_conditions.html"
            rel="noopener noreferrer"
            aria-label="Open Terms and Privacy Policy in a new tab"
          >
            Terms and Privacy Policy
          </a>
        </Typography>
      </div>
    </div>
  )
}

export default Login
